import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { GraphQLFormattedError } from 'graphql';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

type ProtectedRouteProps = {
  element: any;
};

const ProtectedRoute = ({
  element: Component,
  ...rest
}: ProtectedRouteProps) => {
  const [auth, setAuth] = React.useState<boolean | undefined>(undefined);
  const [idToken, setIdToken] = React.useState<string | undefined>(undefined);
  const location = useLocation();

  React.useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        if (session && session.tokens) {
          setAuth(true);
          setIdToken(session.tokens.idToken?.toString());
        } else {
          setAuth(false);
          setIdToken(undefined);
        }
      })
      .catch(() => {
        setAuth(false);
      });
  }, []);

  if (auth === undefined) {
    return <div>Loading...</div>;
  } else {
    const authLink = setContext(async (_, { headers }) => {
      try {
        return {
          headers: {
            ...headers,
            Authorization: idToken?.toString(),
          },
        };
      } catch (error) {
        console.warn('No authenticated user:', error);
        return { headers }; // Pass headers as-is for unauthenticated users
      }
    });

    const errorLink = onError(
      ({ graphQLErrors, networkError, operation, forward }) => {
        console.log(graphQLErrors, networkError);
        if (graphQLErrors) {
          for (const err of graphQLErrors) {
            const formattedError = err as GraphQLFormattedError & {
              errorType?: string;
            };
            if (formattedError.errorType === 'UnauthorizedException') {
              // Error code for unauthenticated errors (401/403)
              fetchAuthSession()
                .then((session) => {
                  // Get a new token
                  const newJwtToken = session.tokens?.accessToken.toString();
                  // Retry the request with the new token
                  operation.setContext(({ headers }: { headers: object }) => ({
                    headers: {
                      ...headers,
                      Authorization: newJwtToken,
                    },
                  }));
                  forward(operation);
                })
                .catch((e) => {
                  // Failed to refresh token, handle appropriately
                  console.error('Failed to refresh token', e);
                });
            }
          }
        }

        if (networkError) {
          console.log(`[Network error]: ${networkError}`);
          fetchAuthSession()
            .then((session) => {
              // Get a new token
              const newJwtToken = session.tokens?.accessToken.toString();
              // Retry the request with the new token
              operation.setContext(({ headers }: { headers: object }) => ({
                headers: {
                  ...headers,
                  Authorization: newJwtToken,
                },
              }));
              forward(operation);
            })
            .catch((e) => {
              // Failed to refresh token, handle appropriately
              console.error('Failed to refresh token', e);
            });
        }
      },
    );

    const httpLink = new HttpLink({ uri: process.env.REACT_APP_API_URL! });
    const client = new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, httpLink]),
      connectToDevTools: true,
      cache: new InMemoryCache({ addTypename: false }),
    });
    return auth && idToken ? (
      <ApolloProvider client={client}>
        <Component {...rest} />
      </ApolloProvider>
    ) : (
      <Navigate to="/login" state={{ from: location }} replace />
    );
  }
};

export default ProtectedRoute;
